import { Component, OnDestroy, OnInit, ViewChild, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { Router } from '@angular/router';
import { DistributionSlice } from '@app/features/v3ndor/core/models/dashboard.model';
import { SavedFilter } from '@app/features/v3ndor/core/models/saved-filter.model';
import { SavedFilterService } from '@app/features/v3ndor/core/services/saved-filter.service';
import { TimeEntryMetricsModel } from '@app/features/v3ndor/core/models/time-entry-metrics.model';
import {
  TimeEntryApiLog,
  buildTimeEntryMetrics,
  flattenTimeEntries,
} from '@app/features/v3ndor/core/services/time-entry-metrics.builder';
import { API_BASE_URL, TicketService } from '@app/features/v3ndor/core/services/ticket.service';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { SwitchThemeService } from '@app/shared/components/switch-theme/switch-theme.service';
import { ToastService } from '@teorema/patron';
import { FilterField, FilterOperator, FindOptions, Ticket, TicketLog } from 'src/app/models';
import { TcTicketLogService, TcTicketService, UserService } from 'src/app/shared/services';
import { constants } from 'src/app/shared/utils/constants';
import { environment } from 'src/environments/environment';
import { buildTicketLogContextMenu } from './ticket-log-context-menu.util';
import {
  PERIODO_PADRAO,
  PeriodoAtalho,
  TICKET_LOG_FILTERS_STORAGE_KEY,
  TICKET_LOG_SAVED_FILTERS_KEY,
  TicketLogFilterParams,
  isTicketLogFilterValid,
  resolvePeriodo,
} from './ticket-log-filter-params';

/** Jornada esperada por dia útil. Base das barras de meta e da cobertura. */
const META_DIARIA_HORAS = 8;

/**
 * Teto real do backend: `QueryHelper::setSize()` corta em `config('max_limit', 500)`
 * e não existe `config/max_limit.php`. Pedir 1000 nunca trouxe 1000 — trazia 500
 * em silêncio. Por isso a busca pagina em laço: com totais na tela, um recorte
 * truncado deixa de ser "faltam cards" e passa a ser um número errado.
 */
const TAMANHO_PAGINA = 500;

/** Guarda contra período absurdo: 20 páginas = 10 mil apontamentos. */
const MAX_PAGINAS = 20;

@Component({
  selector: 'app-ticket-log-history',
  templateUrl: './ticket-log-history.component.html',
  styleUrls: ['./ticket-log-history.component.scss'],
  standalone: false,
  // `TicketService` e `SavedFilterService` são `@Injectable()` sem `providedIn` e
  // dependem de `API_BASE_URL`: fora do módulo v3ndor, quem os usa precisa
  // provê-los. O conjunto completo está em `V3NDOR_SERVICE_PROVIDERS`.
  providers: [
    TicketService,
    SavedFilterService,
    ConfirmationService,
    { provide: API_BASE_URL, useValue: environment.apiUrl },
  ],
})
export class TicketLogHistoryComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastService);
  private readonly tcTicketLogService = inject(TcTicketLogService);
  private readonly tcTicketService = inject(TcTicketService);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly savedFilterService =
    inject<SavedFilterService<TicketLogFilterParams>>(SavedFilterService);
  private readonly layoutService = inject(LayoutService);
  private readonly switchThemeService = inject(SwitchThemeService);

  private readonly themeSignal = toSignal(this.switchThemeService.currentTheme, {
    initialValue: this.switchThemeService.currentTheme.value,
  });
  /** O wrapper do v3ndor só define as variáveis `--v-*` dentro destas classes. */
  readonly currentThemeClass = computed(() =>
    this.themeSignal() === 'dark' ? 'tw-dark-theme' : 'tw-light-theme'
  );

  loading = false;
  user?: any;
  data: any[] = [];

  filters: TicketLogFilterParams = {};
  filterPanelVisible = false;

  /** Cores por tipo de apontamento, para a faixa lateral de cada linha. */
  readonly ticketLogTypes = constants.ticketcenter.ticketLogTypes;

  /**
   * Um único estado para TODOS os cards: o botão de cada card expande e recolhe
   * o conjunto inteiro, não só o próprio. É o comportamento pedido — quem abre o
   * histórico quer varrer o dia, não abrir card por card.
   */
  expandedAll = false;

  metrics?: TimeEntryMetricsModel;
  metricsModalVisible = false;

  /** Apontamentos que o backend cortou, se o laço de páginas esbarrou no teto. */
  registrosOmitidos = 0;

  historyModalVisible = false;
  selectedTicketId: string | null = null;
  selectedTicketNumber: string | null = null;

  /** UM menu para o quadro inteiro — ver `contextMenuRequest` no card. */
  @ViewChild('ticketMenu') ticketMenu?: ContextMenu;
  contextMenuItems: MenuItem[] = [];

  enableSavedFilters = true;
  savedFilters: SavedFilter<TicketLogFilterParams>[] = [];
  activeSavedFilterId: string | null = null;
  /** Recorte de antes de aplicar um filtro salvo, para restaurar ao desativá-lo. */
  private previousFilters: TicketLogFilterParams | null = null;

  constructor() {
    this.layoutService.setPadding('bottom', '0');
    this.layoutService.setBlockContentStyle('overflow-x', 'hidden !important');
  }

  ngOnInit() {
    setTimeout(async () => {
      await this.setUser();
      this.filters = this.restoreFilters();

      if (this.enableSavedFilters) {
        this.loadSavedFilters();
      }

      // Recorte inválido (primeira visita sem storage) abre o painel em vez de
      // deixar a tela vazia sem dizer o que falta.
      if (isTicketLogFilterValid(this.filters, new Date())) {
        this.filterTicketLogs();
      } else {
        this.filterPanelVisible = true;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Filtros
  // ---------------------------------------------------------------------------

  onEditFilters() {
    this.filterPanelVisible = true;
  }

  onFilterPanelVisibleChange(visible: boolean) {
    this.filterPanelVisible = visible;
  }

  onFiltersChange(filters: TicketLogFilterParams) {
    // Mexer no filtro à mão desfaz o vínculo com o chip salvo: manter o chip
    // aceso descrevendo um recorte que já não é o dele faria a barra mentir.
    this.activeSavedFilterId = null;
    this.previousFilters = null;
    this.applyFilters(filters);
  }

  /** Troca de atalho de período preserva o resto do recorte. */
  onPeriodChange(period: PeriodoAtalho) {
    this.activeSavedFilterId = null;
    this.previousFilters = null;

    const { date_from, date_to, ...resto } = this.filters;
    this.applyFilters({ ...resto, period });
  }

  private applyFilters(filters: TicketLogFilterParams) {
    this.filters = filters;
    this.persistFilters(filters);
    this.filterTicketLogs();
  }

  private restoreFilters(): TicketLogFilterParams {
    const salvo = this.lerDoStorage();
    if (salvo && isTicketLogFilterValid(salvo, new Date())) return salvo;
    return this.defaultFilters();
  }

  /**
   * O usuário logado, esta semana. O período é o ATALHO, não datas fixas — a
   * tela abre sempre na semana corrente sem ninguém reconfigurar.
   */
  private defaultFilters(): TicketLogFilterParams {
    return {
      user_ids: this.user?.id ? [this.user.id] : [],
      user_labels: this.user?.name ? [this.user.name] : [],
      period: PERIODO_PADRAO,
    };
  }

  private lerDoStorage(): TicketLogFilterParams | null {
    try {
      const bruto = localStorage.getItem(TICKET_LOG_FILTERS_STORAGE_KEY);
      return bruto ? (JSON.parse(bruto) as TicketLogFilterParams) : null;
    } catch {
      // Storage indisponível ou JSON corrompido não pode derrubar a tela: cai no default.
      return null;
    }
  }

  private persistFilters(filters: TicketLogFilterParams) {
    try {
      localStorage.setItem(TICKET_LOG_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      /* modo privado / storage cheio — perder a persistência não é motivo de erro visível */
    }
  }

  /**
   * Traduz o filtro da tela nos `FilterField` que `TcTicketLogService` espera.
   * A consulta é a MESMA de antes — o que mudou foi só quem monta o recorte.
   */
  private toFindOptions(page: number, periodo: { from: string; to: string }): FindOptions {
    const filtros: FilterField[] = [
      new FilterField({
        name: 'user_id',
        label: 'Usuário',
        multiple: true,
        operator: FilterOperator.in,
        value: this.filters.user_ids ?? [],
      }),
      new FilterField({
        name: 'start_date',
        label: 'Data',
        type: 'betweenDates',
        operator: FilterOperator.between,
        value: [periodo.from, periodo.to],
      }),
      // Sem isto o chamado não vem aninhado e a tela não tem o que agrupar.
      new FilterField({
        name: 'include_ticket',
        label: '',
        type: 'boolean',
        operator: FilterOperator.has,
        value: true,
      }),
    ];

    if (this.filters.customer_ids?.length) {
      filtros.push(
        new FilterField({
          name: 'customer',
          label: 'Cliente',
          multiple: true,
          operator: FilterOperator.in,
          value: this.filters.customer_ids,
        })
      );
    }

    return {
      page,
      perPage: TAMANHO_PAGINA,
      orderBy: 'start_date',
      orderDirection: 'asc',
      filters: filtros,
    };
  }

  // ---------------------------------------------------------------------------
  // Carga
  // ---------------------------------------------------------------------------

  async filterTicketLogs() {
    // Resolvido UMA vez e repassado: com atalho dinâmico, chamar `resolvePeriodo`
    // de novo lá dentro poderia devolver outro intervalo se a consulta
    // atravessasse a meia-noite — as páginas e as métricas ficariam desalinhadas.
    const periodo = resolvePeriodo(this.filters, new Date());
    if (!periodo || !this.filters.user_ids?.length) {
      this.filterPanelVisible = true;
      return;
    }

    this.loading = true;
    this.registrosOmitidos = 0;

    const logs = await this.fetchAllTicketLogs(periodo);

    if (logs) {
      // A ORDEM IMPORTA: `processTicketLogs` muta a lista (faz `delete log.ticket`
      // para poupar memória e reatribui `ticket_logs`). O motor tem que ver a
      // resposta crua — depois dela, metade das linhas não tem mais chamado.
      this.buildMetrics(logs, periodo);
      this.processTicketLogs(logs);
    }

    this.loading = false;
  }

  /**
   * Traz TODAS as páginas do recorte. O backend corta em 500 por página
   * independentemente do que se peça, e a tela antiga lia só a primeira —
   * período largo perdia apontamentos sem nenhum aviso.
   */
  private async fetchAllTicketLogs(periodo: {
    from: string;
    to: string;
  }): Promise<TicketLog[] | null> {
    const acumulado: TicketLog[] = [];
    let pagina = 1;
    let totalNoServidor = 0;

    while (pagina <= MAX_PAGINAS) {
      const response = await this.tcTicketLogService
        .findAll(this.toFindOptions(pagina, periodo))
        .catch(e => {
          console.error(e);
          this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
          return null;
        });

      // Falha no meio da paginação: devolver o que já veio produziria totais
      // menores que a realidade, sem ninguém saber que faltou pedaço.
      if (!response) {
        return pagina === 1 ? null : this.comAviso(acumulado, totalNoServidor);
      }

      acumulado.push(...(response.data ?? []));
      totalNoServidor = response.total ?? response.meta?.total ?? totalNoServidor;

      const ultimaPagina = response.last_page ?? response.meta?.last_page ?? pagina;
      if (pagina >= ultimaPagina) {
        return acumulado;
      }
      pagina++;
    }

    return this.comAviso(acumulado, totalNoServidor);
  }

  /**
   * Registra quantos apontamentos ficaram de fora para a tela poder avisar. Um
   * total parcial exibido como se fosse completo é pior que não ter total.
   */
  private comAviso(acumulado: TicketLog[], totalNoServidor: number): TicketLog[] {
    this.registrosOmitidos = Math.max(0, totalNoServidor - acumulado.length);
    return acumulado;
  }

  private buildMetrics(logs: TicketLog[], periodo: { from: string; to: string }) {
    // O cast atravessa duas descrições do MESMO registro: `TicketLog` (modelo
    // legado, que declara `start_date` como `Date | number` embora a API mande
    // string) e `TimeEntryApiLog` (o que o endpoint realmente devolve). Quem
    // concilia é `flattenTimeEntries`, que aceita os três formatos de data em
    // runtime — o cast só cala o compilador sobre uma diferença já tratada.
    this.metrics = buildTimeEntryMetrics(flattenTimeEntries(logs as unknown as TimeEntryApiLog[]), {
      hoje: new Date(),
      metaDiariaHoras: META_DIARIA_HORAS,
      periodoInicio: new Date(`${periodo.from}T00:00:00`),
      periodoFim: new Date(`${periodo.to}T00:00:00`),
    });
  }

  processTicketLogs(logs: TicketLog[]) {
    this.data = [];
    let lastLabel;
    let lastTicketId;

    logs.forEach(log => {
      if (log.start_date && log.ticket) {
        let date = new Date(log.start_date);
        let ticketId = log.ticket.id;
        let label =
          date.getFullYear().toString().padStart(4, '0') +
          (date.getMonth() + 1).toString().padStart(2, '0') +
          date.getDate().toString().padStart(2, '0');

        if (!this.data[label]) {
          this.data[label] = [];
        }

        if (label === lastLabel && ticketId === lastTicketId) {
          delete log.ticket; //Para poupar memória
          this.data[label][this.data[label].length - 1].ticket_logs.push(log);
        } else {
          log.ticket.ticket_logs = [log];
          this.data[label].push(log.ticket);
        }

        lastLabel = label;
        lastTicketId = ticketId;
      }
    });
  }

  async setUser() {
    this.user = await this.userService.getCurrentUser().catch(e => {
      console.error(e);
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
    });
  }

  // ---------------------------------------------------------------------------
  // Kanban
  // ---------------------------------------------------------------------------

  getObjectDateKeysAsNumbers(object) {
    return this.getObjectDateKeys(object).map(s => Number(s));
  }

  getObjectDateKeys(object): string[] {
    return Object.keys(object)
      .sort((a, b) => {
        return Number(a) - Number(b);
      })
      .reverse();
  }

  dateLabelToDate(value) {
    let temp = value.toString();
    let formatted = temp.substring(0, 4) + '-' + temp.substring(4, 6) + '-' + temp.substring(6, 8);
    let date = new Date(formatted);
    date.setHours(date.getHours() + 3); //Para corrigir fuso horário
    return date;
  }

  /**
   * Clicar no card abre o histórico de apontamentos DAQUELE chamado, em leitura.
   * A saída para a tela de edição do chamado é o menu de contexto (botão direito)
   * do card.
   */
  openTicketLogs(ticket: Ticket) {
    this.selectedTicketId = ticket.id ?? null;
    this.selectedTicketNumber = ticket.ticket_number_teorema ?? null;
    this.historyModalVisible = true;
  }

  onHistoryModalVisibleChange(visible: boolean) {
    this.historyModalVisible = visible;
    if (!visible) {
      this.selectedTicketId = null;
      this.selectedTicketNumber = null;
    }
  }

  /**
   * O clique no botão borbulha até o `(click)` do card, que abre o modal — daí o
   * `stopPropagation`.
   */
  toggleExpandAll(event: MouseEvent) {
    event.stopPropagation();
    this.expandedAll = !this.expandedAll;
  }

  /** Total do chamado NAQUELE dia, já formatado pelo motor. */
  getTicketDayTotal(dateKey: number, ticket: Ticket): string {
    return this.metrics?.duracaoPorDiaChamado.get(`${dateKey}|${ticket.id}`) ?? '';
  }

  getDayBucket(dateKey: number) {
    return this.metrics?.porDia.find(d => d.key === dateKey.toString());
  }

  /** Cor da faixa lateral da linha, por tipo de apontamento. */
  getTypeColor(type?: string): string {
    return this.ticketLogTypes.find((t: any) => t.value === type)?.color ?? 'transparent';
  }

  getTypeLabel(type?: string): string {
    return this.ticketLogTypes.find((t: any) => t.value === type)?.label ?? type ?? '';
  }

  // ---------------------------------------------------------------------------
  // Métricas
  // ---------------------------------------------------------------------------

  openMetrics() {
    this.metricsModalVisible = true;
  }

  /**
   * Clique numa fatia de usuário vira filtro NESTA tela. Só usuário tem alvo: é
   * a única distribuição cuja chave é um id de verdade, então o filtro reproduz
   * exatamente o que a fatia mostrava.
   */
  onMetricSliceActivate(slice: DistributionSlice) {
    const userId = slice.target?.filters?.['user_id'];
    if (typeof userId !== 'string') return;

    this.metricsModalVisible = false;
    this.onFiltersChange({ ...this.filters, user_ids: [userId], user_labels: [slice.label] });
  }

  // ---------------------------------------------------------------------------
  // Menu de contexto (botão direito no card)
  // ---------------------------------------------------------------------------

  /**
   * Monta o menu para o chamado clicado e abre o ÚNICO `p-contextMenu` da tela.
   * Um menu por card seria uma instância por chamado visível, para um popup que
   * só pode estar aberto uma vez.
   */
  onCardContextMenu(event: MouseEvent, ticket: Ticket) {
    this.contextMenuItems = buildTicketLogContextMenu(ticket, {
      ticketService: this.tcTicketService,
      buildTicketUrl: t =>
        // Sem `queryParams: { source: 'sprint' }` que a sprint usa: esse
        // parâmetro faz o "voltar" da tela de detalhe ir para /v3ndor/sprint,
        // que não é de onde o usuário veio.
        this.router.serializeUrl(this.router.createUrlTree(['/v3ndor/chamados', t.id])),
      notify: mensagem => this.toastService.show({ color: 'success', message: mensagem }),
    });

    this.ticketMenu?.show(event);
  }

  // ---------------------------------------------------------------------------
  // Filtros salvos
  // ---------------------------------------------------------------------------

  private loadSavedFilters() {
    this.savedFilterService.list(TICKET_LOG_SAVED_FILTERS_KEY).subscribe({
      next: filtros => (this.savedFilters = filtros),
      error: () => (this.savedFilters = []),
    });
  }

  onApplySavedFilter(filtro: SavedFilter<TicketLogFilterParams>) {
    // Guarda o recorte atual para o clique no chip já ativo poder voltar a ele.
    this.previousFilters = { ...this.filters };
    this.applyFilters({ ...filtro.params });
    this.activeSavedFilterId = filtro.id ?? null;
  }

  onDeactivateSavedFilter() {
    // `defaultFilters()`, não `restoreFilters()`: este já persistiu os params do
    // filtro salvo no storage, então lê-lo de volta devolveria o mesmo recorte
    // que se está tentando desfazer.
    const anterior = this.previousFilters ?? this.defaultFilters();
    this.activeSavedFilterId = null;
    this.previousFilters = null;
    this.applyFilters(anterior);
  }

  onSaveFilter(dto: SavedFilter<TicketLogFilterParams>) {
    this.savedFilterService.save(dto, TICKET_LOG_SAVED_FILTERS_KEY).subscribe({
      next: () => {
        this.toastService.show({ color: 'success', message: 'Filtro salvo com sucesso.' });
        this.loadSavedFilters();
      },
      error: () => {
        this.toastService.show({ color: 'error', message: 'Não foi possível salvar o filtro.' });
      },
    });
  }

  onDeleteSavedFilter(filtro: SavedFilter<TicketLogFilterParams>) {
    if (!filtro.id) return;

    this.confirmationService.confirm({
      message: `Remover o filtro salvo "${filtro.name}"?`,
      header: 'Confirmar exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteSavedFilter(filtro),
    });
  }

  private deleteSavedFilter(filtro: SavedFilter<TicketLogFilterParams>) {
    if (!filtro.id) return;

    if (this.activeSavedFilterId === filtro.id) {
      this.activeSavedFilterId = null;
    }

    this.savedFilterService.remove(filtro.id).subscribe({
      next: () => {
        this.savedFilters = this.savedFilters.filter(f => f.id !== filtro.id);
      },
      error: () => {
        this.toastService.show({ color: 'error', message: 'Não foi possível excluir o filtro.' });
      },
    });
  }

  ngOnDestroy(): void {
    this.layoutService.resetPadding('bottom');
    this.layoutService.resetBlockContentStyle('overflow-x');
  }
}
